import { useTranslation } from 'react-i18next';
import { useOrganization } from '@/clients/organization/useOrganization.ts';

type PurchasePriceHints = {
    partnerOrganizationId: number | undefined;
    purchasePrice: number | undefined;
    showZeroPurchasePrice: boolean;
    onZeroPurchasePriceChange: () => void;
};

export function PurchasePriceHints({
    purchasePrice,
    partnerOrganizationId,
    showZeroPurchasePrice,
    onZeroPurchasePriceChange,
}: PurchasePriceHints) {
    if (purchasePrice === undefined || !partnerOrganizationId) {
        return;
    }

    if (purchasePrice < 0) {
        return <NegativePurchasePriceHint partnerOrganizationId={partnerOrganizationId} />;
    }

    if (purchasePrice === 0) {
        return (
            <ZeroPurchasePriceHint
                partnerOrganizationId={partnerOrganizationId}
                isBillingRelevant={showZeroPurchasePrice}
                onClick={onZeroPurchasePriceChange}
            />
        );
    }
}

function NegativePurchasePriceHint({ partnerOrganizationId }: { partnerOrganizationId: number }) {
    const { t } = useTranslation();
    const { data: partnerOrganization } = useOrganization(partnerOrganizationId);

    return (
        <div className="font-copy-sm text-subdued">
            {t('sections.productSelection.productPositionFooter.negativePurchaseInfoText', {
                orgName: partnerOrganization?.name,
            })}
        </div>
    );
}

type ZeroPurchasePriceHintProps = {
    onClick: () => void;
    partnerOrganizationId: number;
    isBillingRelevant: boolean;
};

function ZeroPurchasePriceHint({ onClick, partnerOrganizationId, isBillingRelevant }: ZeroPurchasePriceHintProps) {
    const { t } = useTranslation();
    const { data: partnerOrganization } = useOrganization(partnerOrganizationId);

    const descriptionLabel = isBillingRelevant
        ? t('sections.productSelection.productPositionFooter.purchasePricePositionConfiguration', {
              orgName: partnerOrganization?.name,
          })
        : t('sections.productSelection.productPositionFooter.zeroPurchaseInfoText', {
              orgName: partnerOrganization?.name,
          });

    const buttonLabel = isBillingRelevant
        ? t('sections.productSelection.productPositionFooter.doNotShowPosition')
        : t('sections.productSelection.productPositionFooter.showPosition');

    return (
        <div className="font-copy-sm text-subdued @3xl:flex-row @3xl:justify-between flex flex-col gap-2">
            <span>{descriptionLabel}</span>
            <button
                type="button"
                className="font-copy-sm-strong cursor-pointer whitespace-nowrap border-none bg-transparent p-0"
                onClick={onClick}
                name="zero-purchase-price-toggle"
                aria-checked={isBillingRelevant}
            >
                {buttonLabel}
            </button>
        </div>
    );
}
